import { useId } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { CreativeFormValue } from '../../creativeFormSchema';
import { CreativeFileKind, CreativeHostedMode } from '../../model';
import { CreativeFileField } from './CreativeFileField';

export function CreativeVideoFields({ editMode }: { editMode: boolean }) {
  const form = useFormContext<CreativeFormValue>();
  const { errors } = useFormState({ control: form.control });
  const hosted = useWatch({ control: form.control, name: 'hosted' });
  const file = useWatch({ control: form.control, name: 'file' });
  const hostedId = useId();
  const clickThroughUrlId = useId();
  const vastTagId = useId();
  const vastUrlId = useId();

  return (
    <>
      <DashboardPageFormField label={dashboardT('creatives.hosted')} htmlFor={hostedId}>
        <Select
          disabled={editMode}
          value={hosted}
          onValueChange={(value) => {
            form.setValue('hosted', value as CreativeHostedMode, { shouldDirty: true });
          }}
        >
          <SelectTrigger id={hostedId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CreativeHostedMode.Shigo}>
              {dashboardT('creatives.shigoHosted')}
            </SelectItem>
            <SelectItem value={CreativeHostedMode.VastTag}>
              {dashboardT('creatives.vastTag')}
            </SelectItem>
            <SelectItem value={CreativeHostedMode.VastUrl}>
              {dashboardT('creatives.vastUrl')}
            </SelectItem>
          </SelectContent>
        </Select>
      </DashboardPageFormField>
      {hosted === CreativeHostedMode.Shigo ? (
        <>
          <DashboardPageFormField
            label={dashboardT('creatives.clickThroughUrl')}
            required
            htmlFor={clickThroughUrlId}
            error={errors.clickThroughUrl?.message}
          >
            <Input
              id={clickThroughUrlId}
              type="url"
              required
              {...form.register('clickThroughUrl')}
            />
          </DashboardPageFormField>
          {!editMode ? (
            <CreativeFileField
              kind={CreativeFileKind.Video}
              file={file}
              error={errors.file?.message}
              onFileChange={(value) => {
                form.setValue('file', value, { shouldDirty: true, shouldValidate: true });
              }}
            />
          ) : null}
        </>
      ) : null}
      {hosted === CreativeHostedMode.VastTag ? (
        <DashboardPageFormField
          label={dashboardT('creatives.vastTag')}
          htmlFor={vastTagId}
          required
          error={errors.vastTag?.message}
        >
          <Textarea
            id={vastTagId}
            required
            className="min-h-48 font-mono"
            {...form.register('vastTag')}
          />
        </DashboardPageFormField>
      ) : null}
      {hosted === CreativeHostedMode.VastUrl ? (
        <DashboardPageFormField
          label={dashboardT('creatives.vastUrl')}
          htmlFor={vastUrlId}
          required
          error={errors.vastUrl?.message}
        >
          <Input id={vastUrlId} type="url" required {...form.register('vastUrl')} />
        </DashboardPageFormField>
      ) : null}
    </>
  );
}
